import { useEffect, useState, useRef } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Plus, Upload, FileText, Trash2, X, Loader2 } from "lucide-react";

export default function EmployeeDocuments({ employeeId }) {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("Aadhaar");
  const [customCategory, setCustomCategory] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    const [d, c] = await Promise.all([
      api.get(`/employees/${employeeId}/documents`),
      api.get("/documents/categories"),
    ]);
    setDocs(d.data); setCategories(c.data); setLoading(false);
  };

  useEffect(() => { load(); }, [employeeId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category === "Other" && customCategory ? customCategory : category);
      await api.post(`/employees/${employeeId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      load();
    } catch (err) {
      alert("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`); load();
  };

  const downloadUrl = (docId) => {
    const token = localStorage.getItem("access_token");
    return `${BACKEND_URL}/api/documents/${docId}/download?auth=${encodeURIComponent(token || "")}`;
  };

  return (
    <div className="card-flat" data-testid="documents-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="kpi-label">KYC & Records</div>
          <h3 className="font-heading text-xl font-bold mt-1">Documents</h3>
        </div>
        <div className="flex items-center gap-2">
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="doc-category-select"
                  className="px-2 py-1.5 text-sm rounded-md border border-stone-200 bg-white">
            {categories.map((c) => <option key={c}>{c}</option>)}
          </select>
          {category === "Other" && (
            <input value={customCategory} onChange={(e) => setCustomCategory(e.target.value)}
                   placeholder="Custom category" data-testid="doc-custom-category"
                   className="px-2 py-1.5 text-sm rounded-md border border-stone-200 bg-white w-32" />
          )}
          <input ref={fileRef} type="file" onChange={onFile} className="hidden"
                 accept="image/*,application/pdf" data-testid="doc-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-doc-button"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md text-white text-sm font-medium" style={{ background: "var(--color-primary)" }}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>

      {loading ? <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
        <div className="space-y-2" data-testid="documents-list">
          {docs.length === 0 && <div className="text-sm text-stone-500 py-4">No documents uploaded yet.</div>}
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 p-3 rounded-md border border-stone-100 hover:border-primary/30">
              <FileText className="w-5 h-5 text-stone-500" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{d.category}</div>
                <div className="text-xs text-stone-500 truncate">{d.filename} · {(d.size / 1024).toFixed(0)} KB</div>
              </div>
              <a href={downloadUrl(d.id)} target="_blank" rel="noopener noreferrer" data-testid={`view-doc-${d.id}`}
                 className="text-xs font-semibold px-2 py-1 rounded" style={{ color: "var(--color-primary)" }}>
                View
              </a>
              <button onClick={() => remove(d.id)} data-testid={`delete-doc-${d.id}`}
                      className="p-1.5 rounded hover:bg-red-50 text-red-600">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
